import { recordNetworkError } from "./feedbackContext";
import { readSelectedOrganisationId } from "./selectedOrganisationStorage";

/** Shape of the structured error envelope returned by Larasoft Python services.
 * See `lara-shared-utils/src/shared_utils/schemas/errors.py::ErrorResponse`.
 * Older services and FastAPI-default responses may only carry `detail`; the
 * other fields are populated once the service has bumped to shared_utils >=
 * the structured-error-envelope release. */
export interface StructuredApiErrorEnvelope {
  detail?: string;
  error_code?: string;
  hint?: string | null;
  trace_id?: string | null;
  service?: string;
  context?: Record<string, unknown> | null;
}

export class PortalApiError extends Error {
  status: number;
  details?: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = "PortalApiError";
    this.status = status;
    this.details = details;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isPermissionDenied(): boolean {
    return this.status === 403;
  }

  /** Typed view of the structured error envelope. Returns `null` when the
   * server didn't include one (rare; only legacy paths). Callers should
   * treat every field as optional. */
  get envelope(): StructuredApiErrorEnvelope | null {
    if (!this.details || typeof this.details !== "object") return null;
    const d = this.details as Record<string, unknown>;
    // Heuristic: must look like our envelope. We accept any of the new keys
    // because legacy responses still carry only `detail` and we don't want
    // to mislabel them.
    if (
      "error_code" in d ||
      "trace_id" in d ||
      "hint" in d ||
      "service" in d
    ) {
      return d as StructuredApiErrorEnvelope;
    }
    return null;
  }

  get errorCode(): string | undefined {
    return this.envelope?.error_code;
  }

  get hint(): string | undefined {
    return this.envelope?.hint ?? undefined;
  }

  get traceId(): string | undefined {
    return this.envelope?.trace_id ?? undefined;
  }

  get errorContext(): Record<string, unknown> | undefined {
    return this.envelope?.context ?? undefined;
  }
}

/** Best-effort coercion of any thrown value to a structured envelope view. */
export function asStructuredError(err: unknown): StructuredApiErrorEnvelope | null {
  if (err instanceof PortalApiError) return err.envelope;
  return null;
}

// Storage key for auth session (must match AuthContext)
const AUTH_STORAGE_KEY = "lara-portal-session";

type StoredSession = {
  token: string;
  refreshToken: string;
  user: unknown;
  createdAt: number;
};

function getStoredSession(): StoredSession | null {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as StoredSession;
  } catch {
    return null;
  }
}

// Whose session this tab is working in (AuthContext keeps it current). Storage
// is shared by every tab, so after a sign-in elsewhere it may hold ANOTHER
// user's session; this tab must never retry or refresh with that one, or its
// unsaved work would be saved as someone else. Null = no owner yet (no check).
let sessionOwnerEmail: string | null = null;

export function setSessionOwner(email: string | null): void {
  sessionOwnerEmail = email;
}

/** Same signed-in user? Emails compare case-insensitively. */
export function isSameUser(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  return (a ?? "").trim().toLowerCase() === (b ?? "").trim().toLowerCase();
}

/** Whether a stored session belongs to this tab's user. */
export function isOwnedSession(session: { user?: unknown } | null): boolean {
  if (!session) return false;
  if (sessionOwnerEmail === null) return true;
  return isSameUser((session.user as { email?: string } | null)?.email, sessionOwnerEmail);
}

/** The stored session, only if it belongs to this tab's user. */
function getOwnedStoredSession(): StoredSession | null {
  const session = getStoredSession();
  return isOwnedSession(session) ? session : null;
}

function updateStoredSession(token: string, refreshToken?: string): void {
  const session = getOwnedStoredSession();
  if (session) {
    session.token = token;
    if (refreshToken) {
      session.refreshToken = refreshToken;
    }
    session.createdAt = Date.now();
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
  }
}

/** Clear the stored session, unless it is now another user's. */
function clearStoredSession(): void {
  if (getOwnedStoredSession()) {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  }
}

// Control-plane API URL
// Dev:  VITE_API_URL is unset → resolveBaseUrl() returns "" → paths like
//       /api/v1/auth/token stay relative → Vite proxy catches /api/*
// Prod: VITE_API_URL=https://api.larasoft.global (host only, NO /api/v1)
//       → paths become https://api.larasoft.global/api/v1/auth/token
//
// ALL endpoints passed to portalFetch or used with resolveBaseUrl() MUST
// include /api/v1/ prefix so they work in both environments.

export const resolveBaseUrl = (): string => {
  const raw = import.meta.env.VITE_API_URL;
  if (raw) return raw.trim().replace(/\/+$/, "");
  return "";
};

// Strip secret path tokens before an endpoint is stored anywhere (the feedback
// buffer, logs). The public onboarding capture link's token is a credential —
// it must never ride along into a support ticket or telemetry sink.
const redactSensitivePath = (endpoint: string): string =>
  endpoint.replace(/(\/public\/onboarding\/)[^/?]+/, "$1[redacted]");

// Track if a refresh is in progress to avoid concurrent refreshes
let refreshPromise: Promise<RefreshOutcome> | null = null;

// Custom event for token refresh - allows AuthContext to stay in sync
export const TOKEN_REFRESHED_EVENT = "lara:token-refreshed";

export type TokenRefreshedEvent = CustomEvent<{
  token: string;
  refreshToken?: string;
}>;

// Raised when the refresh token is refused, i.e. the session really has
// ended. The portal shell listens, calls preventDefault() and shows the
// "signed out" dialog over the page, so unsaved edits stay on screen. Only
// when nothing handles it (a /portal page outside the shell) do we fall back
// to the sign-in redirect.
export const SESSION_EXPIRED_EVENT = "lara:session-expired";

export function announceSessionExpired(): void {
  if (typeof window === "undefined") return;
  const event = new CustomEvent(SESSION_EXPIRED_EVENT, { cancelable: true });
  const handled = !window.dispatchEvent(event);
  if (!handled && window.location.pathname.startsWith("/portal")) {
    window.location.href =
      "/portal/sign-in?error=" +
      encodeURIComponent("Session expired. Please sign in again.");
  }
}

/** Statuses /auth/refresh uses to refuse a refresh token (cp auth.py). */
export const REFRESH_REFUSED_STATUSES: ReadonlySet<number> = new Set([401, 403, 404]);

type RefreshOutcome =
  | { status: "renewed"; token: string }
  /** The refresh token was refused: the session has ended. */
  | { status: "rejected" }
  /** Network error or server fault: the session may still be good. */
  | { status: "unavailable" };

async function refreshAccessToken(): Promise<RefreshOutcome> {
  // If already refreshing, wait for that to complete
  if (refreshPromise) {
    return refreshPromise;
  }

  const session = getOwnedStoredSession();
  if (!session?.refreshToken) {
    return { status: "rejected" };
  }

  refreshPromise = (async (): Promise<RefreshOutcome> => {
    try {
      const baseUrl = resolveBaseUrl();
      const response = await fetch(`${baseUrl}/api/v1/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: session.refreshToken }),
      });

      if (!response.ok) {
        if (REFRESH_REFUSED_STATUSES.has(response.status)) {
          clearStoredSession();
          return { status: "rejected" };
        }
        return { status: "unavailable" };
      }

      const data = await response.json();
      if (data.access_token) {
        updateStoredSession(data.access_token, data.refresh_token);

        // Notify listeners (especially AuthContext) that token was refreshed
        // This keeps React state in sync with localStorage
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent(TOKEN_REFRESHED_EVENT, {
              detail: {
                token: data.access_token,
                refreshToken: data.refresh_token,
              },
            }),
          );
        }

        return { status: "renewed", token: data.access_token as string };
      }

      return { status: "unavailable" };
    } catch {
      return { status: "unavailable" };
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Renew the access token ahead of its expiry (useSessionTimeout calls this
 * while the user is active). A refused refresh token announces the session
 * as expired; a network blip just reports "unavailable" so the caller retries.
 */
export async function renewAccessToken(): Promise<RefreshOutcome["status"]> {
  const outcome = await refreshAccessToken();
  if (outcome.status === "rejected") {
    announceSessionExpired();
  }
  return outcome.status;
}

type PortalFetchOptions = {
  method?: "GET" | "HEAD" | "POST" | "PUT" | "DELETE" | "PATCH";
  body?: unknown;
  token?: string;
  headers?: HeadersInit;
  cache?: RequestCache;
  signal?: AbortSignal;
  /** Keep the request alive past page unload (for fire-and-forget beacons). */
  keepalive?: boolean;
};

async function doFetch(
  path: string,
  method: string,
  headers: Headers,
  payload: string | FormData | undefined,
  cache: RequestCache,
  signal?: AbortSignal,
  keepalive?: boolean,
): Promise<Response> {
  return fetch(path, {
    method,
    headers,
    body: payload,
    cache,
    signal,
    keepalive,
  });
}

// ---------------------------------------------------------------------------
// Internal: shared fetch logic with auth, org headers, and token refresh.
// All public fetch functions delegate here for consistent behaviour.
// ---------------------------------------------------------------------------
async function fetchWithAuth(
  endpoint: string,
  options: PortalFetchOptions = {},
): Promise<Response> {
  const baseUrl = resolveBaseUrl();

  // Strip trailing slashes from the path portion to prevent FastAPI
  // redirect_slashes from issuing 307s that leak the internal Host header.
  // e.g. "/api/v1/groups/?foo=bar" → "/api/v1/groups?foo=bar"
  const normalised = endpoint.replace(/\/(?=\?|$)/, "");

  const path = normalised.startsWith("http")
    ? normalised
    : `${baseUrl}${normalised.startsWith("/") ? "" : "/"}${normalised}`;
  const method = options.method ?? "GET";

  const isFormData = options.body instanceof FormData;

  const buildHeaders = (token?: string): Headers => {
    const headers = new Headers({
      Accept: "application/json",
      // Skip Content-Type for FormData — browser sets it with boundary
      ...(method === "GET" || method === "HEAD" || isFormData
        ? {}
        : {
            "Content-Type": "application/json",
          }),
    });

    // Automatically include organisation ID for RBAC authorization. This
    // tab's own selection, never another tab's (selectedOrganisationStorage).
    const selectedOrgId = readSelectedOrganisationId();
    if (selectedOrgId) {
      headers.set("X-Platform-Organisation-Id", selectedOrgId);
    }

    if (options.headers) {
      const extra = new Headers(options.headers);
      extra.forEach((value, key) => {
        headers.set(key, value);
      });
    }

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    return headers;
  };

  const payload =
    method === "GET" || method === "HEAD" || options.body === undefined
      ? undefined
      : isFormData
        ? (options.body as FormData)
        : typeof options.body === "string"
          ? options.body
          : JSON.stringify(options.body);

  const cache = options.cache ?? "no-store";

  // First attempt with provided token
  let response = await doFetch(
    path,
    method,
    buildHeaders(options.token),
    payload,
    cache,
    options.signal,
    options.keepalive,
  );

  // On 401, try to refresh the token and retry once
  if (response.status === 401 && options.token) {
    // Check if another concurrent request already refreshed the token
    // while we were in-flight. If so, retry with the stored token directly
    // instead of triggering another refresh.
    const currentSession = getOwnedStoredSession();
    let newToken: string | null = null;

    if (currentSession && currentSession.token !== options.token) {
      // Token was already refreshed by another concurrent request
      newToken = currentSession.token;
    } else {
      // We need to refresh ourselves (or join an in-progress refresh)
      const outcome = await refreshAccessToken();
      if (outcome.status === "renewed") {
        newToken = outcome.token;
      } else if (outcome.status === "rejected") {
        // The session has ended. Never a hard redirect here: the shell
        // shows the signed-out dialog over the page so edits survive.
        announceSessionExpired();
      }
      // "unavailable": a transient failure; the 401 goes back to the caller
      // and the session is left alone.
    }

    if (newToken) {
      // Retry with new token
      response = await doFetch(
        path,
        method,
        buildHeaders(newToken),
        payload,
        cache,
        options.signal,
        options.keepalive,
      );
    }
  }

  return response;
}

// ---------------------------------------------------------------------------
// Public fetch helpers — use these instead of raw fetch() everywhere.
// ---------------------------------------------------------------------------

/** Fetch JSON from the portal API. Handles auth, org headers, and token refresh. */
export async function portalFetch<T = unknown>(
  endpoint: string,
  options: PortalFetchOptions = {},
): Promise<T> {
  const response = await fetchWithAuth(endpoint, options);

  const text = await response.text();
  const parsed = text ? safeJsonParse(text) : null;

  if (!response.ok) {
    // Record for feedback widget context
    recordNetworkError({
      url: redactSensitivePath(endpoint),
      status: response.status,
      method: options.method ?? "GET",
    });

    // Handle FastAPI validation errors (detail is array) and regular errors (detail is string)
    let message: string;
    if (parsed?.detail) {
      if (Array.isArray(parsed.detail)) {
        // Pydantic validation errors: [{type, loc, msg, input}, ...]
        message = parsed.detail
          .map((e: { msg?: string; loc?: string[] }) => {
            const field = e.loc
              ?.filter((l) => l !== "body")
              .join(".")
              .replace(/_/g, " ");
            const msg = e.msg || "Validation error";
            return field ? `${field}: ${msg}` : msg;
          })
          .join(", ");
      } else if (typeof parsed.detail === "object" && parsed.detail !== null) {
        // FastAPI HTTPException with a structured dict detail (e.g. our
        // config endpoint's {message, unknown_keys, declared_keys}). Prefer
        // its `.message`, else a compact JSON so the user sees something
        // useful instead of "[object Object]". Full object stays on
        // PortalApiError.details for callers that want the structured form.
        const d = parsed.detail as Record<string, unknown>;
        message =
          typeof d.message === "string" ? d.message : JSON.stringify(d);
      } else {
        message = String(parsed.detail);
      }
    } else if (parsed?.message) {
      message = String(parsed.message);
    } else {
      message = response.statusText || `Request failed (${response.status})`;
    }

    // A 401 that survived fetchWithAuth's refresh has already been handled
    // there (renewed and retried, or announced as expired).

    throw new PortalApiError(message, response.status, parsed);
  }

  return (parsed as T) ?? ({} as T);
}

/**
 * Fetch a raw Response from the portal API. Handles auth, org headers, and
 * token refresh. Use when you need response headers (e.g. Content-Disposition)
 * or non-JSON body parsing.
 *
 * Throws PortalApiError on non-ok responses.
 */
export async function portalFetchResponse(
  endpoint: string,
  options: PortalFetchOptions = {},
): Promise<Response> {
  const response = await fetchWithAuth(endpoint, options);

  if (!response.ok) {
    recordNetworkError({
      url: redactSensitivePath(endpoint),
      status: response.status,
      method: options.method ?? "GET",
    });

    // Try to extract error detail from JSON body
    let message = response.statusText || "Request failed";
    try {
      const text = await response.text();
      const parsed = safeJsonParse(text);
      if (parsed?.detail) {
        message = String(parsed.detail);
      }
    } catch {
      // Body may already be consumed or not parseable
    }

    throw new PortalApiError(message, response.status);
  }

  return response;
}

/**
 * Fetch a binary Blob from the portal API. Handles auth, org headers, and
 * token refresh. Use for file downloads (PDFs, Excel, images, etc).
 *
 * Throws PortalApiError on non-ok responses.
 */
export async function portalFetchBlob(
  endpoint: string,
  options: PortalFetchOptions = {},
): Promise<Blob> {
  const response = await portalFetchResponse(endpoint, options);
  return response.blob();
}

const safeJsonParse = (value: string): Record<string, unknown> | null => {
  try {
    return JSON.parse(value) as Record<string, unknown>;
  } catch {
    return null;
  }
};

